import Link from "next/link"
import { ArrowUpRight } from "lucide-react"
import type { ReactNode } from "react"

interface FeaturedAccentCardProps {
  /** Destination URL when the card is clicked */
  href: string
  /** Icon rendered in the top-left icon tile */
  icon: ReactNode
  /** Uppercase eyebrow above the title (e.g. "ENERGY & COST") */
  eyebrow: string
  /** Big black headline (e.g. the product / tool name) */
  title: string
  /** One-paragraph supporting copy */
  description: string
  /** Optional top-right badge (e.g. "● Most Used"). Rendered inside orange pill. */
  badge?: string
  /** CTA link text shown bottom-right (default: "Start now") */
  ctaLabel?: string
  /** Additional classes on the outer link — use for col-span in grid contexts */
  className?: string
}

/**
 * FeaturedAccentCard — oversized promotional card on fridge-blue with a
 * diffused orange glow in the top-right corner. Used to spotlight one
 * "most used" / "featured" item inside a larger grid of standard cards.
 *
 * Sits cleanly in a grid as `sm:col-span-2 lg:col-span-2` — the caller
 * passes that via `className`. The card stretches to the row height set by
 * the grid, so it works next to shorter neighbor cards.
 */
export function FeaturedAccentCard({
  href,
  icon,
  eyebrow,
  title,
  description,
  badge,
  ctaLabel = "Start now",
  className = "",
}: FeaturedAccentCardProps) {
  return (
    <Link
      href={href}
      className={[
        "group relative overflow-hidden rounded-md bg-[#0469f0] text-white p-6 sm:p-8 flex flex-col justify-between min-h-[240px] sm:min-h-[280px] transition-transform duration-200 hover:-translate-y-1 hover:shadow-xl",
        className,
      ].join(" ")}
    >
      {/* Diffused orange accent glow */}
      <div
        aria-hidden
        className="absolute -top-20 -right-20 w-[340px] h-[340px] rounded-full pointer-events-none"
        style={{ background: "rgba(255,153,81,0.35)", filter: "blur(50px)" }}
      />

      {/* Top row — icon tile + badge */}
      <div className="relative flex justify-between items-start gap-4">
        <div className="w-14 h-14 rounded-lg bg-white/15 border border-white/25 text-white flex items-center justify-center flex-shrink-0">
          {icon}
        </div>
        {badge && (
          <span className="bg-[#ff9951] text-[var(--color-brand-action-text)] text-[10px] font-bold uppercase tracking-[0.12em] px-[11px] py-[5px] rounded-full">
            {badge}
          </span>
        )}
      </div>

      {/* Headline block */}
      <div className="relative mt-8 sm:mt-12">
        <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
          {eyebrow}
        </div>
        <h3
          className="mt-2.5 font-black tracking-[-0.03em] leading-none"
          style={{ fontSize: "clamp(28px, 3vw, 40px)" }}
        >
          {title}
        </h3>
        <p className="mt-3.5 text-[14px] sm:text-[15px] text-white/85 leading-[1.5] max-w-[480px]">
          {description}
        </p>
        <div className="mt-5 flex items-center text-[13px] text-white/85">
          <span className="ml-auto font-semibold text-white inline-flex items-center gap-1.5">
            {ctaLabel} <ArrowUpRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  )
}
